import React, { useEffect, useState } from 'react';
import { getCookie } from '../utils/common';
import { dashboardAPI, exportCSVAPI } from '../services/api/dashboard';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

const initialState = {
    tickets: [],
    review_score_avg: '',
    total_tickets: 0,
    filters: {
        channel: [],
        scorecards: [],
        managers: [],
        all_team_members: [],
        team_members: [{ name: "All", key: "" }],
        contact_reasons: [],
        all_sub_contact_reasons: [],
        sub_contact_reasons: [{ name: "All", key: "" }],
        languages: [],
        customer_efforts: [],
        report_periods: [],
    },
    selected_filter: {
        channel: { name: "All", key: "" },
        scorecard: { name: "All", key: "" },
        managers: [{ name: "All", key: "" }],
        team_members: [{ name: "All", key: "" }],
        contact_reason: { name: "All", key: "" },
        sub_contact_reason: { name: "All", key: "" },
        language: { name: "All", key: "" },
        customer_effort: { name: "All", key: "" },
        report_period: { name: "Yesterday", key: "yesterday" },
        custom_date_from: null,
        custom_date_to: null
    },
};

interface TicketData {
    contact_reason: string;
    assignee_email: string;
    review_score: number;
    date: string;
}

type SortConfig = {
    key: keyof TicketData;
    direction: 'asc' | 'desc';
} | null;

export const DashboardContext = React.createContext({
    ...initialState,
    isLoading: true,
    isFilterLoading: true,
    isFilterApplied: false,
    isExported: false,
    exportedMsg: '',
    isExportedError: false,
    sortConfig: null as SortConfig,
    getDashboardReport: () => { },
    handleSelect: (name: string, option: any, isMulti: Boolean) => { },
    handleSelectDate: (name: string, date: string) => { },
    applyFilter: (queryString: string) => { },
    clearFilter: () => { },
    onSort: (columnName: keyof TicketData) => {},
    exportCsv: () => {},
    searchTickets: (thread_id: string)=> {}
});

const DashboardContextProvider = (props: any) => {
    const [state, setState] = useState(initialState);
    const [isLoading, setIsLoading] = useState(true);
    const [isExported, setIsExported] = useState(false);
    const [isExportedError, setIsExportedError] = useState(false);
    const [exportedMsg, setExportedMsg] = useState('');
    const [isFilterLoading, setIsFilterLoading] = useState(true);
    const [isFilterApplied, setIsFilterApplied] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [isClearFilter, setIsClearFilter] = useState(false);
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);

    useEffect(() => {
        if (isClearFilter) {
            getDashboardReport();
            setIsClearFilter(false);
        }
    }, [isClearFilter]);

    useEffect(() => {
        searchParams.set('report_period', state.selected_filter.report_period.key);
        setSearchParams(searchParams);
        getDashboardReport();
    }, [searchParams.get('limit'), searchParams.get('offset')]);

    useEffect(() => {
        if (sortConfig) {
            const params = new URLSearchParams(searchParams);
            params.set('sort_by', `${sortConfig.key}:${sortConfig.direction}`);
            setSearchParams(params);
            getDashboardReport(params.toString());
        }
    }, [sortConfig]);

    useEffect(() => {
        getFilters();
    }, []);

    const onSort = (columnName: keyof TicketData) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === columnName && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnName, direction });
    };

    const getFilters = () => {
        const tenantSlug = getCookie('current_tenant');
        setIsFilterLoading(true);
        dashboardAPI(tenantSlug).getAll?.(`?return_filters=true`)
            .then((resp) => {
                setState(prevState => ({
                    ...prevState,
                    filters: {
                        channel: resp.filters.communication_modes,
                        scorecards: resp.filters.scorecards,
                        contact_reasons: resp.filters.contact_reasons,
                        all_sub_contact_reasons: resp.filters.sub_contact_reasons,
                        sub_contact_reasons: resp.filters.sub_contact_reasons,
                        customer_efforts: resp.filters.customer_efforts,
                        languages: resp.filters.languages,
                        managers: resp.filters.managers,
                        report_periods: resp.filters.report_periods,
                        all_team_members: resp.filters.team_members,
                        team_members: resp.filters.team_members,
                    }
                }));
                setIsFilterLoading(false);
            })
            .catch(err => {
                setIsFilterLoading(false);
                console.log(err);
            });
    }

    const getDashboardReport = (params: string = '') => {
        const tenantSlug = getCookie('current_tenant');
        const searchParam = params || searchParams.toString();
        setIsLoading(true);
        dashboardAPI(tenantSlug).getAll?.(`?${searchParam}`)
            .then((resp) => {
                setState(prevState => ({
                    ...prevState,
                    tickets: resp.data.hits,
                    review_score_avg: resp.data.review_score_avg,
                    total_tickets: resp.data.total_tickets
                }));
                setIsLoading(false);
            })
            .catch(err => {
                setIsLoading(false);
                console.log(err);
            });
    }

    const searchTickets = (thread_id: string = '') => {
        const tenantSlug = getCookie('current_tenant');
        setIsLoading(true);
        dashboardAPI(tenantSlug).getAll?.(`?thread_id=${thread_id}`)
            .then((resp) => {
                setState(prevState => ({
                    ...prevState,
                    tickets: resp.data.hits,
                    review_score_avg: resp.data.review_score_avg,
                    total_tickets: resp.data.total_tickets
                }));
                setIsLoading(false);
            })
            .catch(err => {
                setIsLoading(false);
                console.log(err);
            });
    }

    const exportCsv = () => {
        const tenantSlug = getCookie('current_tenant');
        exportCSVAPI(tenantSlug).getAll?.(`?${searchParams.toString()}`)
            .then((resp) => {
                setIsExported(true);
                setExportedMsg(resp.message);
                setTimeout(()=>{
                    setIsExported(false);
                    setExportedMsg('');
                },2000)
            })
            .catch(err => {
                console.log(err);
                setIsExportedError(true);
                setTimeout(()=>{
                    setIsExportedError(false);
                },2000)
            });
    }

    const handleSelect = (name: string, option: any, isMulti: Boolean) => {
        setState(prevState => ({
            ...prevState,
            selected_filter: {
                ...prevState.selected_filter,
                [name]: option
            }
        }));
        if (!isMulti) {
            searchParams.set(name, option.key);
        } else {
            const keys = option.map((opt: any) => opt.key).filter((key: string) => key !== '');
            if (keys.includes('')) {
                searchParams.delete(name);
            } else {
                searchParams.set(name, keys.join(','));
            }
        }
        setSearchParams(searchParams);
        setIsFilterApplied(false);
        if (name === 'contact_reason') {
            const selectedCRKeys = option.map((cr: any) => cr.key);
            const filtered_sub_contact_reasons = state.filters.all_sub_contact_reasons.filter((cr: any) =>
                selectedCRKeys.includes(cr.contact_reason_key)
            );
            setState(prevState => ({
                ...prevState,
                filters: {
                    ...prevState.filters,
                    sub_contact_reasons: filtered_sub_contact_reasons,
                }
            }));
        }

        if (name === 'manager') {
            const selectedManagerKeys = option.map((manager: any) => manager.key);
            const filtered_team_members = state.filters.all_team_members.filter((member: any) =>
                selectedManagerKeys.includes(member.manager_key)
            );

            setState(prevState => ({
                ...prevState,
                filters: {
                    ...prevState.filters,
                    team_members: filtered_team_members,
                }
            }));
        }
    }

    const handleSelectDate = (name: string, date: any) => {
        setState(prevState => ({
            ...prevState,
            selected_filter: {
                ...prevState.selected_filter,
                [name]: date
            }
        }));
        searchParams.set(name, moment(date).format('YYYY-MM-DD'));
        setSearchParams(searchParams);
        setIsFilterApplied(false);
    }

    const applyFilter = (queryString: string) => {
        setIsFilterApplied(true);
        getDashboardReport();
    }

    const clearFilter = () => {
        setIsFilterApplied(false);
        const preservedParams = new URLSearchParams();
        preservedParams.set('report_period', initialState.selected_filter.report_period.key);
        setSearchParams(preservedParams);
        setState(prevState => ({
            ...prevState,
            selected_filter: initialState.selected_filter
        }));
        setIsClearFilter(true);
    }

    const contextValue = {
        ...state,
        isLoading,
        isFilterLoading,
        isFilterApplied,
        isExported,
        isExportedError,
        exportedMsg,
        sortConfig,
        getDashboardReport,
        handleSelect,
        handleSelectDate,
        applyFilter,
        clearFilter,
        onSort,
        exportCsv,
        searchTickets
    };

    return (
        <DashboardContext.Provider value={contextValue}>
            {props.children}
        </DashboardContext.Provider>
    );
}

export default DashboardContextProvider;
